import {createSelector} from 'reselect';
import {determineAvailability} from '../utils/determineAvailability';
import {
    getListing,
    getTripData,
    getDatePicker
} from './store';
import {
    getArrivedFromExternalSiteWithoutDates,
    getLocale,
    getSystemClientSideRendered
} from './system';
import getListingPageModalStates from './modalStates';
import {getRateSummary} from './listing';

const getInquiryModalOpen = createSelector(
    [getListingPageModalStates],
    (listingPageModalStates) => {
        return listingPageModalStates.inquiryModalOpen;
    }
);

const getTripDataArriveDateText = createSelector(
    [getTripData],
    (tripData) => {
        return tripData.arriveDateText;
    }
);

const getTripDataCurrentDate = createSelector(
    [getTripData],
    (tripData) => {
        return tripData.currentDate;
    }
);

const getDatePickerArriveDateText = createSelector(
    [getDatePicker],
    (datePickerData) => {
        return datePickerData.arriveDateText;
    }
);

const getShouldShowRates = createSelector(
    [getRateSummary],
    (rateSummary) => {
        const hasRates = rateSummary && rateSummary.rentNightsConverted && rateSummary.rentNightsConverted.length;
        const hasUSCurrency = rateSummary && rateSummary.travelerCurrency === 'USD';
        const hasAccurateRate = rateSummary && rateSummary.accurateRate;

        // These could be undefined, which leads to this selector returning undefined instead of a truethy value.
        return !!hasRates && !!hasAccurateRate && !!hasUSCurrency;
    }
);

/*
    The calendar, and date pickers, will need to display what days are:
    a) Available for checkin.
    b) Available to checkout on, based on different criteria.
    c) Already occupied.

    The utility to calculate all that data is calculateCalendarAvailabilityData.
    It is extracted in order for other apps and processes to use, and for testing.

    ARE YOU ADDING TO THE SELECTOR DEPENDENCIES?
    Yes?  Only add a dep for individual attributes, and not the whole object.
*/
const getDatePickerAvailabilityData = createSelector(
    [getListing, getTripDataCurrentDate, getDatePickerArriveDateText, getLocale, getShouldShowRates, getSystemClientSideRendered, getArrivedFromExternalSiteWithoutDates, getInquiryModalOpen],
    (listing, tripDataCurrentDate, datePickerArriveDateText, locale, shouldShowRates, clientSideRendered, arrivedFromExternalSiteWithoutDates, inquiryModalOpen) => {
        // don't run any of the availability determination logic during SSR for perf reasons, as well as the fact that we defer
        // parsing of the visitor's trip data until the client-side render
        if (!clientSideRendered) {
            return {};
        }

        return determineAvailability({
            listing,
            tripDataCurrentDate,
            selectedArriveDate: datePickerArriveDateText,
            locale,
            shouldShowRates,
            useEmptyAvailability: inquiryModalOpen ? false : arrivedFromExternalSiteWithoutDates
        });
    }
);

const getTripDataAvailabilityData = createSelector(
    [getListing, getTripDataCurrentDate, getTripDataArriveDateText, getLocale, getShouldShowRates, getSystemClientSideRendered],
    (listing, tripDataCurrentDate, tripDataArriveDateText, locale, shouldShowRates, clientSideRendered) => {
        // don't run any of the availability determination logic during SSR for perf reasons, as well as the fact that we defer
        // parsing of the visitor's trip data until the client-side render
        if (!clientSideRendered) {
            return {};
        }

        return determineAvailability({
            listing,
            tripDataCurrentDate,
            selectedArriveDate: tripDataArriveDateText,
            locale,
            shouldShowRates
        });
    }
);

const getAvailabilityData = createSelector(
    [getDatePickerAvailabilityData, getTripDataAvailabilityData],
    (datePickerAvailabilityData, tripDataAvailabilityData) => {
        return {
            datePickerAvailabilityData,
            calendarAvailabilityData: tripDataAvailabilityData
        };
    }
);

export default getAvailabilityData;
export {
    getAvailabilityData,
    getDatePickerAvailabilityData,
    getShouldShowRates,
    getTripDataAvailabilityData
};
